"use client";
import { useEffect, useRef, useState } from "react";
import AnimatedLink from "@/components/AnimatedLink";
import AnimatedText from "@/components/AnimatedText";
import AnimatedBlock from "@/components/AnimatedBlock";
import useDarkMode from "@/components/useDarkMode";
import translations from "@/lib/translations";
import { usePathname } from "next/navigation";
import Link from "next/link";

function MenuLinks({ lang }) {
	const pathname = usePathname();
	const isCurrent = (path) => pathname === `/${lang}${path}`;
	const pathFR = pathname.replace(/^\/(fr|en)/, "/fr");
	const pathEN = pathname.replace(/^\/(fr|en)/, "/en");

	return (
		<AnimatedText variant="bounce">
			<ul>
				<li>
					<AnimatedLink href={`/${lang}/work`} className={isCurrent("/work") ? "current" : ""}>
						{translations.nav.work[lang]}
					</AnimatedLink>
				</li>
				<li>
					<AnimatedLink href={`/${lang}/services`} className={isCurrent("/services") ? "current" : ""}>
						{translations.nav.services[lang]}
					</AnimatedLink>
				</li>
				<li>
					<AnimatedLink href={`/${lang}/about`} className={isCurrent("/about") ? "current" : ""}>
						{translations.nav.about[lang]}
					</AnimatedLink>
				</li>
				<li>
					<AnimatedLink href={`/${lang}/contact`} className={isCurrent("/contact") ? "current" : ""}>
						{translations.nav.contact[lang]}
					</AnimatedLink>
				</li>
				<li>
					<Link href={`https://paulfactory.itch.io`} target="_blank" rel="noopener noreferrer">
						{translations.nav.play[lang]}
					</Link>
				</li>
				<li>
					<div className="lang-switch">
						<a href={pathFR} className={lang === "fr" ? "current-language" : ""}>
							FR
						</a>
						<a href={pathEN} className={lang === "en" ? "current-language" : ""}>
							EN
						</a>
					</div>
				</li>
			</ul>
		</AnimatedText>
	);
}

export default function Header({ lang }) {
	const pathname = usePathname();
	const isCurrent = (path) => pathname === `/${lang}${path}`;
	const [isDark, setIsDark] = useDarkMode();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const navRef = useRef(null);
	// remove showInAnim
	const [showOutAnim, setShowOutAnim] = useState(false);
	const [menuAnim, setMenuAnim] = useState(null);

	const lottieRef = useRef();
	const [showLottie, setShowLottie] = useState(false);

	const logoLottieRef = useRef();
	const [logoAnim, setLogoAnim] = useState(null);

	const [canToggleTheme, setCanToggleTheme] = useState(true);

	const [hasScrolled, setHasScrolled] = useState(false);

	useEffect(() => {
		import("../public/animations/transition-white.json").then((mod) => {
			setMenuAnim(mod.default);
		});
	}, []);

	useEffect(() => {
		if (showLottie && menuAnim && lottieRef.current) {
			const lottie = require("lottie-web");
			const animInstance = lottie.loadAnimation({
				container: lottieRef.current,
				renderer: "svg",
				loop: false,
				autoplay: false,
				animationData: menuAnim,
				rendererSettings: {
					preserveAspectRatio: "none",
				},
			});
			lottieRef.current.lottie = animInstance;
			return () => animInstance.destroy();
		}
	}, [showLottie, menuAnim]);

	useEffect(() => {
		import("../public/animations/logo.json").then((mod) => {
			setLogoAnim(mod.default);
		});
	}, []);

	useEffect(() => {
		if (logoAnim && logoLottieRef.current) {
			const lottie = require("lottie-web");
			const instance = lottie.loadAnimation({
				container: logoLottieRef.current,
				renderer: "svg",
				loop: false,
				autoplay: false,
				animationData: logoAnim,
				rendererSettings: {
					preserveAspectRatio: "xMidYMid meet",
				},
			});
			instance.goToAndStop(0, true);
			logoLottieRef.current.lottie = instance;
			return () => instance.destroy();
		}
	}, [logoAnim]);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (isMenuOpen && navRef.current && !navRef.current.contains(e.target)) {
				setIsMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isMenuOpen]);

	// Remove showInAnim and related setTimeout
	useEffect(() => {
		if (!isMenuOpen) {
			setShowOutAnim(true);
			setTimeout(() => setShowOutAnim(false), 600);
		}
	}, [isMenuOpen]);

	useEffect(() => {
		if (isMenuOpen) {
			setShowLottie(true); // Ne joue rien ici
		} else if (showLottie && lottieRef.current) {
			lottieRef.current.lottie?.playSegments([50, 100], true);
			setTimeout(() => setShowLottie(false), 1000); // durée anim out
		}
	}, [isMenuOpen]);

	// Joue l'anim in après que le composant Lottie est monté
	useEffect(() => {
		if (showLottie && isMenuOpen && lottieRef.current) {
			requestAnimationFrame(() => {
				lottieRef.current.lottie?.playSegments([0, 50], true);
			});
		}
	}, [showLottie, isMenuOpen]);

	useEffect(() => {
		const observer = new MutationObserver(() => {
			if (document.body.classList.contains("page-enter")) {
				setIsMenuOpen(false);
			}
		});
		observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			setHasScrolled(window.scrollY > 2);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<header className={`${isMenuOpen ? "menu-open" : ""} ${hasScrolled ? "scrolled" : ""}`}>
			<AnimatedBlock className="icon logo">
				<div className="logo-wrapper">
					<AnimatedLink href={`/${lang}`} className={isCurrent(`/${lang}`) ? "current" : ""}>
						<div
							className="lottie-logo"
							ref={logoLottieRef}
							onMouseEnter={() => {
								const lottie = logoLottieRef.current?.lottie;
								if (lottie) {
									lottie.stop();
									lottie.play();
								}
							}}
						/>
						<svg viewBox="0 0 60 60" fill="none" className="bg-logo" xmlns="http://www.w3.org/2000/svg">
							<path d="M0 30C0 17.8238 0 11.7357 2.97504 7.39268C4.16204 5.65988 5.65988 4.16204 7.39268 2.97504C11.7357 0 17.8238 0 30 0V0C42.1762 0 48.2643 0 52.6073 2.97504C54.3401 4.16204 55.838 5.65988 57.025 7.39268C60 11.7357 60 17.8238 60 30V30C60 42.1762 60 48.2643 57.025 52.6073C55.838 54.3401 54.3401 55.838 52.6073 57.025C48.2643 60 42.1762 60 30 60V60C17.8238 60 11.7357 60 7.39268 57.025C5.65988 55.838 4.16204 54.3401 2.97504 52.6073C0 48.2643 0 42.1762 0 30V30Z" />
						</svg>
					</AnimatedLink>
				</div>
			</AnimatedBlock>
			<AnimatedBlock className="burger">
				<button className="burger-button" onClick={() => setIsMenuOpen(!isMenuOpen)}>
					<span></span>
					<span></span>
					<span></span>
				</button>
			</AnimatedBlock>
			<nav ref={navRef}>
				<MenuLinks lang={lang} />
				<button className="close-button" onClick={() => setIsMenuOpen(false)}>
					<span></span>
				</button>
			</nav>				
			<AnimatedBlock className="icon color-mode">
				<div>
					<button onClick={() => {
						if (!canToggleTheme) return;
						setIsDark(!isDark);
						setCanToggleTheme(false);
						setTimeout(() => setCanToggleTheme(true), 800);
					}}>
						<svg className="night-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
							<path d="M41.18,25.7c-.61-.27-1.32-.11-1.75.4-1.41,1.65-3.27,2.74-5.38,3.15-5.15,1.01-10.15-2.35-11.16-7.49-.89-4.52,1.58-9.01,5.88-10.68.62-.24,1.01-.86.95-1.52-.06-.66-.54-1.21-1.19-1.34-2.23-.46-4.5-.47-6.75-.03-9.27,1.82-15.34,10.85-13.51,20.12.88,4.49,3.46,8.37,7.26,10.92,2.86,1.92,6.15,2.92,9.52,2.92,1.11,0,2.23-.11,3.34-.33,7.21-1.41,12.7-7.26,13.67-14.55.09-.66-.27-1.3-.87-1.57Z" />
						</svg>
						<svg className="light-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
							<path d="M25,10.61c-7.93,0-14.39,6.45-14.39,14.39s6.45,14.39,14.39,14.39,14.39-6.45,14.39-14.39-6.46-14.39-14.39-14.39Z" />
							<path d="M25,7.07c1.1,0,2-.9,2-2v-2.77c0-1.1-.9-2-2-2s-2,.9-2,2v2.77c0,1.1.9,2,2,2Z" />
							<path d="M9.49,12.32c.39.39.9.59,1.41.59s1.02-.2,1.41-.59c.78-.78.78-2.05,0-2.83l-1.96-1.96c-.78-.78-2.05-.78-2.83,0-.78.78-.78,2.05,0,2.83l1.96,1.96Z" />
							<path d="M5.07,23h-2.77c-1.1,0-2,.9-2,2s.9,2,2,2h2.77c1.1,0,2-.9,2-2s-.9-2-2-2Z" />
							<path d="M9.49,37.68l-1.96,1.96c-.78.78-.78,2.05,0,2.83.39.39.9.59,1.41.59s1.02-.2,1.41-.59l1.96-1.96c.78-.78.78-2.05,0-2.83-.78-.78-2.05-.78-2.83,0Z" />
							<path d="M25,42.93c-1.1,0-2,.9-2,2v2.77c0,1.1.9,2,2,2s2-.9,2-2v-2.77c0-1.1-.9-2-2-2Z" />
							<path d="M40.51,37.68c-.78-.78-2.05-.78-2.83,0-.78.78-.78,2.05,0,2.83l1.96,1.96c.39.39.9.59,1.41.59s1.02-.2,1.41-.59c.78-.78.78-2.05,0-2.83l-1.96-1.96Z" />
							<path d="M47.7,23h-2.77c-1.1,0-2,.9-2,2s.9,2,2,2h2.77c1.1,0,2-.9,2-2s-.9-2-2-2Z" />
							<path d="M39.09,12.91c.51,0,1.02-.2,1.41-.59l1.96-1.96c.78-.78.78-2.05,0-2.83-.78-.78-2.05-.78-2.83,0l-1.96,1.96c-.78.78-.78,2.05,0,2.83.39.39.9.59,1.41.59Z" />
						</svg>
					</button>
				</div>
			</AnimatedBlock>
			<AnimatedBlock className="icon bg">
				<div></div>
			</AnimatedBlock>
			{showLottie && <div className="menu-lottie" ref={lottieRef} />}
		</header>
	);
}
